import React, { useCallback, useEffect, useState } from "react";
import StepIndicator from "../subComponent/step-indicator/StepIndicator";
import { SuccessAlertComponent } from "../odsCalloutBox/SuccessAlertComponent";
import { FormSectionElements, ProgramContainer } from "../../entities/formTemplateDataType";
import { WarningAlertComponent } from "../odsCalloutBox/WarningAlertComponent";
import FeedbackComponent from "../subComponent/FeedbackComponent";
import { FeedbackStatus } from "../../entities/Constants";
import TrackPageView from "../../utilities/TrackPageView";
import createAccount from "../../../public/assets/images/create-account.png";
import renewalReminders from "../../../public/assets/images/renewal-reminders.png";
import moreServices from "../../../public/assets/images/more-services.png";
import organDonation from "../../../public/assets/images/card2.png";
import { styled } from "styled-components";
import { getUserProfileFromSession } from "../../services/CxpProfileService";
import SessionStorageService from "../../services/SessionStorageService";
import { normalizeUrl } from "../../utilities/URLUtils";
import HelmetComponent from "../../commonComponent/HelmetComponent";
import { getEnvVar } from "../../config/RuntimeConfig";
import { VECON_CONTENT, VECON_PDF_DOCUMENTS, getVeconContent, getVeconDisplayName, getVeconPdfOrder } from "../../entities/VeconConstants";
import ReCaptchaV3 from "../captcha/CaptchComponent";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

interface Props {
    programContainer: ProgramContainer;
    isEng: boolean;
    transactionId: string;
    Visible: boolean;
    callback: (p: any) => void;
    submissionDate?: string;
    renewalResponse?: any;
    captchaToken?: string;
}

const Divider = styled.div`
    width: 100%;
    height: 0;
    border-top: 3px solid #333;
    opacity: 1;
    transform: rotate(0deg);
    margin: 50px auto;
`;

const DocumentLink = styled.a`
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: 'Open Sans', serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 25.6px;
    color: #0066cc;
    text-decoration: underline;
    margin-bottom: 8px;

    &:hover {
        text-decoration: none;
    }
`;

const DocumentIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
`;

const StepSection = styled.div`
    margin-bottom: 24px;

    &:last-child {
        margin-bottom: 0;
    }

    h6 {
        margin-bottom: 16px;
    }
`;

const DownloadButtonsContainer = styled.div`
    display: inline-flex;
    flex-direction: column;
    gap: 8px;

    .ontario-button {
        width: 100%;
    }

    @media (max-width: 40em) {
        display: flex;
    }
`;


interface VECONDownloadActionsProps {
    programContainer: ProgramContainer;
    isEng: boolean;
    renewalResponse: any;
    content: ReturnType<typeof getVeconContent>;
    pdfDocuments: Array<{ fileName: string; blobFileName: string; displayName: string }>;
    renderDocumentIcon: () => React.ReactNode;
    transactionId: string;
}

const VECONDownloadActions: React.FC<VECONDownloadActionsProps> = ({
    programContainer,
    isEng,
    renewalResponse,
    content,
    pdfDocuments,
    renderDocumentIcon,
    transactionId,
}) => {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const isRecaptchaDisabled = getEnvVar('DISABLED_RECAPTCHA') === "true";
    const [loading, setLoading] = useState(false);

    const getFreshToken = useCallback(async (): Promise<string | undefined> => {
        if (isRecaptchaDisabled) return undefined;

        for (let attempt = 0; attempt < 5; attempt++) {
            if (executeRecaptcha) {
                try {
                    return await executeRecaptcha("downloadVECON");
                } catch (err) {
                    console.error("executeRecaptcha failed:", err);
                }
            }

            console.warn("executeRecaptcha not ready, retrying...");
            await new Promise((res) => setTimeout(res, 1000));
        }

        console.error("executeRecaptcha is still not ready after retries");
        return undefined;
    }, [executeRecaptcha, isRecaptchaDisabled]);

    const downloadFromBlobUrl = useCallback(async (blobFileName: string, displayName: string) => {
        try {
            const token = await getFreshToken();
            const accessToken = SessionStorageService.getPSToken();
            const downloadUrl = programContainer.APIBasePath + programContainer.downloadAPI;

            const response = await fetch(`${downloadUrl}/${encodeURIComponent(blobFileName)}`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'x-recaptcha-token': token || '',
                },
            });

            const buffer = await response.arrayBuffer();
            const blob = new Blob([buffer], { type: 'application/pdf' });
            const blobUrl = window.URL.createObjectURL(blob);

            const anchor = document.createElement('a');
            anchor.href = blobUrl;
            anchor.download = `${displayName}.pdf`;
            anchor.style.display = 'none';
            document.body.appendChild(anchor);
            anchor.click();

            setTimeout(() => {
                document.body.removeChild(anchor);
                URL.revokeObjectURL(blobUrl);
            }, 1000);
        } catch (err) {
            console.error('Error downloading document:', err);
        }
    }, [getFreshToken, programContainer.APIBasePath, programContainer.downloadAPI]);

    const logDownloadEvent = useCallback(async () => {
        try {
            const profile = getUserProfileFromSession();
            const eventType = profile?.userId ? 'VECON_DOCUMENT_DOWNLOAD_USER' : 'VECON_DOCUMENT_DOWNLOAD_GUEST';
            await fetch(programContainer.analyticsAPI, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${SessionStorageService.getPSToken()}`,
                },
                body: JSON.stringify({
                    EventSource: 'VECON',
                    EventType: eventType,
                    TransactionId: transactionId,
                    jsonData: null,
                    ProfileId: profile?.userId || '',
                }),
            });
        } catch (err) {
            console.error('Error logging download event:', err);
        }
    }, [programContainer.APIBasePath]);

    const downloadAllDocuments = useCallback(async () => {
        if (loading) return;
        setLoading(true);
        try {
            logDownloadEvent();
            const pdfUrls = renewalResponse?.pdfUrls || [];
            for (const pdf of pdfUrls) {
                const displayName = getVeconDisplayName(pdf.pdfFileName, isEng);
                await downloadFromBlobUrl(pdf.blobFileName, displayName);
                await new Promise(resolve => setTimeout(resolve, 500));
            }
        } finally {
            setLoading(false);
        }
    }, [loading, renewalResponse, isEng, downloadFromBlobUrl, logDownloadEvent]);

    const downloadChecklist = useCallback(async () => {
        if (loading) return;
        setLoading(true);
        try {
            const langSuffix = isEng ? '_EN.pdf' : '_FR.pdf';
            const checklistDoc = VECON_PDF_DOCUMENTS.find(doc => doc.fileName.includes('VECON_Checklist') && doc.fileName.endsWith(langSuffix));
            const checklist = renewalResponse?.pdfUrls?.find((pdf: { pdfFileName: string; }) => pdf.pdfFileName === checklistDoc?.fileName);
            if (checklist) {
                const displayName = getVeconDisplayName(checklist.pdfFileName, isEng);
                await downloadFromBlobUrl(checklist.blobFileName, displayName);
            }
        } finally {
            setLoading(false);
        }
    }, [loading, isEng, renewalResponse, downloadFromBlobUrl]);

    const handleDocumentClick = useCallback((e: React.MouseEvent, blobFileName: string, displayName: string) => {
        e.preventDefault();
        if (loading) return;
        downloadFromBlobUrl(blobFileName, displayName);
    }, [loading, downloadFromBlobUrl]);

    return (
        <>
            <DownloadButtonsContainer className="ontario-margin-bottom-32-!">
                <button
                    className="ontario-button ontario-button--primary"
                    onClick={downloadAllDocuments}
                    disabled={loading}
                    aria-disabled={loading}
                >
                    {content.downloadAllDocuments}
                </button>
                <button
                    className="ontario-button ontario-button--secondary"
                    onClick={downloadChecklist}
                    disabled={loading}
                    aria-disabled={loading}
                >
                    {content.downloadChecklist}
                </button>
            </DownloadButtonsContainer>

            <div>
                <h2>{content.whatToDoNext}</h2>
                <p>{content.whatToDoNextIntro}</p>

                <StepSection>
                    <h6>{content.step1Title}</h6>
                    <p>{content.step1Content}</p>
                    <p>{content.step1Instruction}</p>

                    <div>
                        {pdfDocuments.map((doc, index) => (
                            <DocumentLink
                                key={doc.fileName || index}
                                href="#"
                                onClick={(e) => handleDocumentClick(e, doc.blobFileName, doc.displayName)}
                                aria-disabled={loading}
                            >
                                {renderDocumentIcon()}
                                {doc.displayName}
                            </DocumentLink>
                        ))}
                    </div>
                </StepSection>

                <StepSection>
                    <h6>{content.step2Title}</h6>
                    <p>{content.step2Content}</p>
                </StepSection>

                <StepSection>
                    <h6>{content.step3Title}</h6>
                    <p>{content.step3Content}</p>
                </StepSection>

                <StepSection>
                    <h6>{content.step4Title}</h6>
                    <p>{content.step4Content}</p>
                </StepSection>

                <StepSection>
                    <h6>{content.step5Title}</h6>
                    <p>{content.step5Content}</p>
                </StepSection>

                <StepSection>
                    <h6>{content.step6Title}</h6>
                    <p>{content.step6Content}</p>
                </StepSection>
            </div>
        </>
    );
};

export class VECONOrderConfirmationComponent extends React.Component<Props> {
    field: FormSectionElements;

    constructor(props: Props) {
        super(props);

        this.field = new FormSectionElements();
        this.field.ENUTitle = VECON_CONTENT.en.successAlertTitle;
        this.field.FRATitle = VECON_CONTENT.fr.successAlertTitle;
    }

    componentDidMount() {
        window.scrollTo(0, 0);
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<{}>, snapshot?: any): void {
        if (!this.props.Visible) return;
        const title = "your-application-has-been-submitted";
        const isEng = window.location.href.indexOf("/fr/") === -1;

        let preUrl = window.location.origin + "/on-form/vecon/review-your-information";
        if (!prevProps.isEng) {
            preUrl = window.location.origin + "/on-form/fr/vecon/review-your-information";
        }

        sessionStorage.setItem("previousUrl", preUrl);

        let urlString = window.location.origin + "/on-form/vecon/" + title;

        if (!isEng) {
            urlString = window.location.origin + "/on-form/fr/vecon/" + title;
        }
        window.history.pushState({}, "", normalizeUrl(urlString));

        if (this.props.Visible && sessionStorage.getItem("languageChange") !== "true" && sessionStorage.getItem("VECONSummaryFired") !== "true") {
            TrackPageView(this.props.programContainer);
            sessionStorage.setItem("VECONSummaryFired", "true");
            sessionStorage.removeItem("languageChange");
        }
    }

    componentWillUnmount(): void {
        sessionStorage.removeItem("VECONSummaryFired");
        sessionStorage.removeItem("languageChange");
    }

    renderDocumentIcon = () => (
        <DocumentIcon>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16.5 6.75V17.5C16.5 19.71 14.71 21.5 12.5 21.5C10.29 21.5 8.5 19.71 8.5 17.5V5.5C8.5 4.12 9.62 3 11 3C12.38 3 13.5 4.12 13.5 5.5V15.5C13.5 16.05 13.05 16.5 12.5 16.5C11.95 16.5 11.5 16.05 11.5 15.5V6.75H10V15.5C10 16.88 11.12 18 12.5 18C13.88 18 15 16.88 15 15.5V5.5C15 3.29 13.21 1.5 11 1.5C8.79 1.5 7 3.29 7 5.5V17.5C7 20.54 9.46 23 12.5 23C15.54 23 18 20.54 18 17.5V6.75H16.5Z" fill="#0066CC"/>
            </svg>
        </DocumentIcon>
    );

    render() {
        if (!this.props?.Visible) return null;

        const content = getVeconContent(this.props.isEng);
        const profile = getUserProfileFromSession();
        const isCxpUser = !!profile?.userId;

        const surveyUrlParams = {
            src: 'link',
            sglocale: this.props.isEng ? 'en' : 'fr-ca',
        };

        let backToLabel;
        let backToUrl;
        if (isCxpUser) {
            backToLabel = content.backToHome;
            backToUrl = getEnvVar('PUBLIC_CXP_WEB_BASE_PATH') +
                "/auth/signin?redirectUri=" +
                (this.props?.isEng ? "" : "/fr") +
                "/dashboard";
        } else {
            backToLabel = content.backToOntario;
            backToUrl = this.props?.isEng
                ? "https://www.ontario.ca/page/government-ontario"
                : "https://www.ontario.ca/fr/page/gouvernement-de-lontario";
        }

        const submissionDate = this.props.submissionDate || new Date().toISOString().split('T')[0];

        const pdfDocuments = (this.props.renewalResponse?.pdfUrls || [])
            .map(pdf => ({
                fileName: pdf.pdfFileName,
                blobFileName: pdf.blobFileName,
                displayName: getVeconDisplayName(pdf.pdfFileName, this.props.isEng),
            })).sort((a, b) => getVeconPdfOrder(a.fileName) - getVeconPdfOrder(b.fileName));

        const tabTitle = content.tabTitle;

        return (
            <div>
                <HelmetComponent title={tabTitle} />
                <div>
                    <StepIndicator
                        withbackbtn={true}
                        sectionSequence={-1}
                        title={backToLabel}
                        backToUrl={backToUrl}
                        programContainer={this.props?.programContainer}
                        isEng={this.props?.isEng}
                        callback={(currentStep) => {
                            this.setState({ stepperIndex: currentStep });
                        }}
                    />
                </div>

                <div>
                    <SuccessAlertComponent
                        field={this.field}
                        title={content.successAlertTitle}
                        bodyMsg=""
                        isEng={this.props.isEng}
                        Visible={true}
                    />
                </div>

                {(!isCxpUser || !this.props.renewalResponse?.saveCopyConsent) && (
                    <div>
                        <WarningAlertComponent
                            title={content.warningAlertTitle}
                            bodyMsg=""
                            isEng={this.props.isEng}
                            Visible={true}
                        />
                    </div>
                )}

                <div>
                    <h1>{content.mainTitle}</h1>
                    <p>{content.orderIdLabel}{this.props.transactionId}</p>
                    <p>{content.dateLabel}{submissionDate}</p>
                    <p>
                        {isCxpUser ? content.instructionTextCxp : content.instructionTextGuest}
                    </p>
                </div>

                <ReCaptchaV3 lang={this.props.isEng ? 'en' : 'fr'}>
                    <VECONDownloadActions
                        programContainer={this.props.programContainer}
                        isEng={this.props.isEng}
                        renewalResponse={this.props.renewalResponse}
                        content={content}
                        pdfDocuments={pdfDocuments}
                        renderDocumentIcon={this.renderDocumentIcon}
                        transactionId={this.props.transactionId}
                    />
                </ReCaptchaV3>

                <div className="ontario-margin-top-32-! ontario-margin-bottom-32-!">
                    <FeedbackComponent
                        programContainer={this.props.programContainer}
                        isEng={this.props.isEng}
                        transactionId={this.props.transactionId}
                        status={FeedbackStatus.Success}
                        isHyperlink={true}
                        params={surveyUrlParams}
                        feedbackTitle={content.feedbackQuestion}
                        feedbackLinkText={content.feedbackLink}
                    />
                </div>

                <Divider />

                <section>
                    <AvailableServicesComponent isEng={this.props.isEng} isCxpUser={isCxpUser} content={content} />
                </section>
            </div>
        );
    }
}

const CardsContainer = styled.ul`
    display: flex;
    flex-wrap: wrap;
    gap: 25px;
    list-style: none;
    padding: 0;
    margin: 0;
    max-width: 1119px;

    @media (max-width: 40em) {
        flex-direction: column;
    }
`;

const Card = styled.li`
    width: 261px;
    height: 387px;
    border-radius: 4px;
    border: 1px solid #cccccc;
    background-color: white;
    box-shadow: 0px 3px 8px 1px rgba(0, 0, 0, 0.4);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    cursor: pointer;
    transition: all 0.3s ease-in-out;

    &:hover {
        box-shadow: 0px 6px 12px 2px rgba(0, 0, 0, 0.35);
    }

    @media (max-width: 40em) {
        width: 100%;
        height: auto;
    }
`;

const CardImageContainer = styled.div`
    height: 184px;
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const CardContent = styled.div`
    padding: 16px 24px;
    flex: 1;
    display: flex;
    flex-direction: column;
`;

const CardLink = styled.a`
    font-family: Raleway, "Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif;
    font-weight: 700;
    font-size: 19px;
    line-height: 29px;
    letter-spacing: 0.4px;
    vertical-align: middle;
    text-decoration: underline;
    text-decoration-style: solid;
    color: #0066CC;
    margin-bottom: 8px;

    &:hover {
        text-decoration: none;
    }
`;

const CardDescription = styled.p`
    font-family: 'Open Sans', sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 25.6px;
    letter-spacing: 0px;
    color: #1A1A1A;
    margin: 0;
`;

const CARDS_CONTENT = {
    en: {
        createAccount: {
            id: 'create-account',
            title: "Create an account",
            description: "You're a few clicks away from creating an account. Create an account to complete services faster in the future.",
            image: createAccount,
            link: "https://services.ontario.ca/web/splash"
        },
        renewalReminders: {
            id: 'renewal-reminders',
            title: "Renewal reminders",
            description: "Get reminders before your licence plate, driver's licence, health card, or Ontario photo card expires.",
            image: renewalReminders,
            link: "https://www.ontario.ca/page/get-serviceontario-renewal-reminders"
        },
        organDonation: {
            id: 'organ-donation',
            title: "Organ donation",
            description: "Help save a life. Join millions of Ontarians by registering as an organ and tissue donor.",
            image: organDonation,
            link: "https://www.ontario.ca/page/organ-and-tissue-donor-registration"
        },
        moreServices: {
            id: 'more-services',
            title: "Explore more services",
            description: "Check out other services you can complete easily from the comfort of your own home.",
            image: moreServices,
            link: "https://www.ontario.ca/page/government-ontario"
        }
    },
    fr: {
        createAccount: {
            id: 'create-account',
            title: "Créer un compte",
            description: "Vous pouvez créer un compte en seulement quelques clics. Créez votre compte pour obtenir des services plus rapidement à l'avenir.",
            image: createAccount,
            link: "https://services.ontario.ca/web/fr/splash"
        },
        renewalReminders: {
            id: 'renewal-reminders',
            title: "Rappels de renouvellement",
            description: "Recevez des rappels avant l'expiration de votre plaque d'immatriculation, de votre permis de conduire, de votre carte santé ou de votre carte-photo de l'Ontario.",
            image: renewalReminders,
            link: "https://www.ontario.ca/fr/page/recevoir-rappels-renouvellement-serviceontario"
        },
        organDonation: {
            id: 'organ-donation',
            title: "Don d'organe",
            description: "Aidez à sauver une vie. Joignez-vous à des millions d'Ontariennes et d'Ontariens en vous inscrivant comme donneur d'organes et de tissus.",
            image: organDonation,
            link: "https://www.ontario.ca/fr/page/inscription-au-don-dorganes-et-de-tissus-humains"
        },
        moreServices: {
            id: 'more-services',
            title: "Explorer d'autres services",
            description: "Jetez un coup d'œil aux autres services que vous pouvez facilement obtenir dans le confort de votre foyer.",
            image: moreServices,
            link: "https://www.ontario.ca/fr/page/gouvernement-de-lontario"
        }
    }
};

interface AvailableServicesProps {
    isEng: boolean;
    isCxpUser: boolean;
    content: typeof VECON_CONTENT['en'];
}

const AvailableServicesComponent: React.FC<AvailableServicesProps> = ({ isEng, isCxpUser, content }) => {
    const cardsContent = CARDS_CONTENT[isEng ? 'en' : 'fr'];

    const cards = isCxpUser
        ? [cardsContent.renewalReminders, cardsContent.organDonation, cardsContent.moreServices]
        : [cardsContent.createAccount, cardsContent.renewalReminders, cardsContent.organDonation, cardsContent.moreServices];

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <>
            <h2>{content.exploreServicesTitle}</h2>
            <CardsContainer>
                {cards.map((card) => (
                    <Card key={card.id}>
                        <CardImageContainer>
                            <img src={card.image} alt={card.title} />
                        </CardImageContainer>
                        <CardContent>
                            <CardLink href={card.link} target="_blank" rel="noopener noreferrer">
                                {card.title}
                            </CardLink>
                            <CardDescription>{card.description}</CardDescription>
                        </CardContent>
                    </Card>
                ))}
            </CardsContainer>
        </>
    );
};
